import { useState } from "react";
import { api, type Strategy } from "../lib/api.js";
import { Drawer, Empty, Notice, useAsync } from "../components/ui.js";
import type { PageProps } from "./Shell.js";

/**
 * Settings profiles push client options to machines. The keys are RustDesk's
 * own option names; the common ones are offered as fields and anything else
 * can be set as a raw key/value pair.
 */
const COMMON_OPTIONS: Array<{ key: string; label: string; hint: string; type: "bool" | "text" }> = [
  { key: "enable-keyboard", label: "Allow keyboard and mouse", hint: "", type: "bool" },
  { key: "enable-clipboard", label: "Allow clipboard", hint: "", type: "bool" },
  { key: "enable-file-transfer", label: "Allow file transfer", hint: "", type: "bool" },
  { key: "enable-audio", label: "Allow audio", hint: "", type: "bool" },
  { key: "enable-tunnel", label: "Allow port tunnels", hint: "", type: "bool" },
  { key: "enable-remote-restart", label: "Allow remote restart", hint: "", type: "bool" },
  { key: "enable-record-session", label: "Allow session recording", hint: "", type: "bool" },
  { key: "allow-remote-config-modification", label: "Allow changing settings remotely", hint: "", type: "bool" },
  { key: "verification-method", label: "Password mode", hint: "use-permanent-password, use-temporary-password or use-both-passwords", type: "text" },
  { key: "approve-mode", label: "Approval mode", hint: "password, click or both", type: "text" },
  { key: "temporary-password-length", label: "One-time password length", hint: "6, 8 or 10", type: "text" },
  { key: "custom-rendezvous-server", label: "ID server", hint: "Overrides the server this machine uses", type: "text" },
  { key: "relay-server", label: "Relay server", hint: "", type: "text" },
  { key: "api-server", label: "API server", hint: "", type: "text" },
];

export default function Strategies({ me, onToggleRail }: PageProps) {
  const [editing, setEditing] = useState<Strategy | "new" | null>(null);
  const list = useAsync(() => api.get<Strategy[]>("/api/console/strategies"), []);
  const canWrite = me.permissions.includes("*") || me.permissions.includes("strategy:write");

  return (
    <>
      <header className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onToggleRail} aria-label="Menu">&#9776;</button>
        <div className="topbar__title">
          <h1>Settings profiles</h1>
          <span className="topbar__hint">{list.data?.length ?? 0} profiles</span>
        </div>
        <div className="topbar__actions">
          {canWrite ? (
            <button className="btn btn--primary btn--sm" onClick={() => setEditing("new")}>New profile</button>
          ) : null}
        </div>
      </header>

      <div className="content">
        <div className="stack">
          {list.error ? <Notice>{list.error}</Notice> : null}
          <div className="notice">
            A profile pushes RustDesk settings to machines. Each machine picks the most specific
            profile that targets it, and applies changes on its next check-in (within 15 seconds).
          </div>

          {!list.loading && !list.data?.length ? (
            <Empty
              title="No profiles yet"
              hint="Create one to enforce settings like password policy or which features are allowed."
              action={canWrite ? <button className="btn btn--primary" onClick={() => setEditing("new")}>Create a profile</button> : null}
            />
          ) : (
            <div className="panel">
              <div className="tablewrap">
                <table className="data">
                  <thead><tr><th>Name</th><th className="num">Settings</th><th>Applies to</th><th style={{ width: 80 }} /></tr></thead>
                  <tbody>
                    {(list.data ?? []).map((s) => (
                      <tr key={s.id}>
                        <td>
                          {s.name}
                          {s.is_default ? <span className="tag tag--live" style={{ marginLeft: 8 }}>Default</span> : null}
                          {s.note ? <div className="small faint">{s.note}</div> : null}
                        </td>
                        <td className="num">{Object.keys(s.options).length}</td>
                        <td className="muted small">
                          {s.targets.length
                            ? s.targets.map((t) => t.target_type.replace("_", " ")).join(", ")
                            : s.is_default ? "Everything without a more specific profile" : <span className="faint">Nothing yet</span>}
                        </td>
                        <td className="actions">
                          {canWrite ? <button className="btn btn--sm" onClick={() => setEditing(s)}>Edit</button> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {editing ? (
        <StrategyDrawer
          strategy={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { list.reload(); setEditing(null); }}
        />
      ) : null}
    </>
  );
}

function StrategyDrawer({
  strategy, onClose, onSaved,
}: { strategy: Strategy | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(strategy?.name ?? "");
  const [note, setNote] = useState(strategy?.note ?? "");
  const [isDefault, setIsDefault] = useState(strategy?.is_default ?? false);
  const [options, setOptions] = useState<Record<string, string>>(strategy?.options ?? {});
  const [customKey, setCustomKey] = useState("");
  const [customValue, setCustomValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k: string, v: string) =>
    setOptions((o) => {
      const next = { ...o };
      if (v === "") delete next[k];
      else next[k] = v;
      return next;
    });

  async function save() {
    setBusy(true); setError("");
    try {
      const body = { name, note, options, is_default: isDefault };
      if (strategy) await api.patch(`/api/console/strategies/${strategy.id}`, body);
      else await api.post("/api/console/strategies", body);
      onSaved();
    } catch (e) { setError((e as Error).message); setBusy(false); }
  }

  return (
    <Drawer
      title={strategy ? `Edit ${strategy.name}` : "New settings profile"}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--primary" onClick={save} disabled={busy || !name.trim()}>
            {busy ? "Saving…" : "Save profile"}
          </button>
          <div className="spacer" />
          {strategy ? (
            <button className="btn btn--danger btn--sm" disabled={busy}
              onClick={() => api.del(`/api/console/strategies/${strategy.id}`).then(onSaved)}>
              Delete
            </button>
          ) : null}
        </>
      }
    >
      <div className="stack">
        {error ? <Notice>{error}</Notice> : null}
        <div className="field">
          <label className="field__label">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div className="field">
          <label className="field__label">Note</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <label className="check">
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          Use for every machine without a more specific profile
        </label>

        <div className="panel">
          <div className="panel__head"><h3>Settings</h3></div>
          <div className="panel__body">
            {COMMON_OPTIONS.map((o) => (
              <div className="field" key={o.key}>
                <label className="field__label">{o.label}</label>
                {o.type === "bool" ? (
                  <select value={options[o.key] ?? ""} onChange={(e) => set(o.key, e.target.value)}>
                    <option value="">Leave as the machine has it</option>
                    <option value="Y">Yes</option>
                    <option value="N">No</option>
                  </select>
                ) : (
                  <input value={options[o.key] ?? ""} onChange={(e) => set(o.key, e.target.value)}
                    placeholder="Leave blank to skip" />
                )}
                {o.hint ? <span className="field__hint">{o.hint}</span> : null}
              </div>
            ))}
          </div>
        </div>

        <div className="panel">
          <div className="panel__head"><h3>Other settings</h3></div>
          <div className="panel__body">
            {Object.entries(options)
              .filter(([k]) => !COMMON_OPTIONS.some((o) => o.key === k))
              .map(([k, v]) => (
                <div className="row" key={k} style={{ marginBottom: "0.375rem" }}>
                  <code className="mono small" style={{ flex: 1 }}>{k}</code>
                  <code className="mono small">{v}</code>
                  <button className="btn btn--ghost btn--sm" onClick={() => set(k, "")}>Remove</button>
                </div>
              ))}
            <div className="row" style={{ marginTop: "0.5rem" }}>
              <input className="mono small" placeholder="option-name" value={customKey}
                onChange={(e) => setCustomKey(e.target.value)} />
              <input className="mono small" placeholder="value" value={customValue}
                onChange={(e) => setCustomValue(e.target.value)} />
              <button className="btn btn--sm" disabled={!customKey.trim()}
                onClick={() => { set(customKey.trim(), customValue); setCustomKey(""); setCustomValue(""); }}>
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
